import React from 'react';
import { Box, Select, MenuItem, FormControl, SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowRightIcon from 'src/assets/images/icons/arrow-right_ico.svg';
import { cn } from 'src/utils/utility';

// ======= UI Elements =======
export const PaginationArrowImage = styled('img')(() => ({
  width: '20px',
  height: '20px',
}));

// ======= Reusable Pagination Components =======

// Component for page size selector
export const PageSizeSelector: React.FC<{
  pageSize: number;
  pageSizeOptions: number[];
  onChangePageSize: (pageSize: number) => void;
}> = ({ pageSize, pageSizeOptions, onChangePageSize }) => (
  <FormControl size='small' sx={{ minWidth: 80, mx: 1 }}>
    <Select
      value={pageSize.toString()}
      onChange={(e: SelectChangeEvent) => {
        const newPageSize = parseInt(e.target.value, 10);
        onChangePageSize(newPageSize);
      }}
      displayEmpty
      className='border border-neutral-20 rounded-6 bg-white'
      sx={{
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        height: '32px',
        fontSize: '14px',
      }}
    >
      {pageSizeOptions.map((size) => (
        <MenuItem key={size} value={size.toString()}>
          {size}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

// Component for items count display
export const ItemsCountDisplay: React.FC<{
  pageSize: number;
  total: number;
  children?: React.ReactNode;
}> = ({ pageSize, total, children }) => (
  <div className='flex items-center text-14 xl:text-16'>
    Showing
    <Box component='span' sx={{ mx: 1 }}>
      {children || pageSize}
    </Box>
    of {total} items
  </div>
);

// Component for page numbers display
export const PageInfoDisplay: React.FC<{
  currentPage: number;
  totalPages: number;
  children?: React.ReactNode;
}> = ({ currentPage, totalPages, children }) => (
  <Box display='flex' gap={1} alignItems='center' className='text-14 xl:text-16'>
    {children}
    <span>Page</span>
    <span>{currentPage}</span>
    <span>of</span>
    <span>{totalPages}</span>
  </Box>
);

// Previous page button
export const PreviousPageButton: React.FC<{
  currentPage: number;
  isDisabled: boolean;
  onChangePage: (page: number) => void;
}> = ({ currentPage, isDisabled, onChangePage }) => (
  <button
    type='button'
    className={cn('w-32 h-32 rounded-6 border border-neutral-20 bg-white flex items-center justify-center', isDisabled && 'opacity-50 cursor-not-allowed')}
    disabled={isDisabled}
    onClick={() => {
      if (!isDisabled) {
        const pageNumber = currentPage > 1 ? currentPage - 1 : 1;
        onChangePage(pageNumber);
      }
    }}
  >
    <span className='rotate-180 origin-center flex items-center justify-center'>
      <PaginationArrowImage src={ArrowRightIcon} alt='Previous' />
    </span>
  </button>
);

// Next page button
export const NextPageButton: React.FC<{
  currentPage: number;
  isDisabled: boolean;
  onChangePage: (page: number) => void;
}> = ({ currentPage, isDisabled, onChangePage }) => (
  <button
    type='button'
    className={cn('w-32 h-32 rounded-6 border border-neutral-20 bg-white flex items-center justify-center', isDisabled && 'opacity-50 cursor-not-allowed')}
    disabled={isDisabled}
    onClick={() => {
      if (!isDisabled) {
        const pageNumber = currentPage + 1;
        onChangePage(pageNumber);
      }
    }}
  >
    <PaginationArrowImage src={ArrowRightIcon} alt='Next' />
  </button>
);

// Full navigation controls (prev/next buttons with page counter)
export const PaginationNavigation: React.FC<{
  currentPage: number;
  totalPages: number;
  onChangePage: (page: number) => void;
}> = ({ currentPage, totalPages, onChangePage }) => {
  const isPreviousDisabled = currentPage === 1;
  const isNextDisabled = currentPage >= totalPages;

  return (
    <div className='flex items-center gap-8'>
      <PageInfoDisplay currentPage={currentPage} totalPages={totalPages} />
      <PreviousPageButton currentPage={currentPage} isDisabled={isPreviousDisabled} onChangePage={onChangePage} />
      <NextPageButton currentPage={currentPage} isDisabled={isNextDisabled} onChangePage={onChangePage} />
    </div>
  );
};
